/**
* Composition for Custom Tools
*/
import md5 from 'md5'
import { v4 as uuidv4 } from 'uuid'

export default function () {
  const { $i18n: i18n } = useNuxtApp()
  const { currentRoute } = useRouter()
  const { confGlobal, confMeta } = useConfig()
  const { layout } = useHelpers()

  /**
  * Custom Cookie
  */
  const useCustomCookie = (name, expires = 60 * 60 * 24 * 30 * 12) => {
    const hash = md5(`${confGlobal.site}_${confGlobal.serverType}_${confGlobal.dev}`).slice(-6)

    return useCookie(`${name}_${hash}`, {
      maxAge: expires
    })
  }

  /**
  * Set Meta Tags
  */
  const setMetaTags = () => {
    useHead({
      viewport: 'width=device-width, initial-scale=1', // maximum-scale=5
      charset: 'utf-8'
    })

    if (layout.value !== 'catalog' && layout.value !== 'product' && layout.value !== 'page' && layout.value !== 'index') {
      const headMeta = {
        title: '',
        meta: [{
          name: 'description',
          content: ''
        },
        {
          name: 'keywords',
          content: ''
        }]
      }

      const routes = currentRoute.value.path.split('/')
      routes.shift()
      if (routes) {
        const lvl1 = confMeta[routes[0]]
        if (lvl1) {
          if (lvl1.meta) {
            headMeta.title = lvl1.meta.title + ' |'
            headMeta.meta.find(n => n.name === 'description').content = lvl1.meta.description
          }

          if (lvl1.sub) {
            const lvl2 = lvl1.sub[routes[1]]
            if (lvl2) {
              if (lvl2.meta) {
                headMeta.title = lvl2.meta.title + ' |'
                headMeta.meta.find(n => n.name === 'description').content = lvl2.meta.description
              }
            }
          }
        }
      }

      useHead(headMeta)

      return true
    }

    return false
  }

  /**
  * Dynamic Title
  * ?utm_term=keyword_unique+gifts+for+customers
  *
  * Task: CD-3835
  */
  const getDynamicTitle = () => {
    if (currentRoute.value.query.utm_term) {
      const urlString = currentRoute.value.query.utm_term.toLowerCase()

      const objectiveObj = {
        'luxury': 'Luxury',
        'custom': 'Custom',
        'personal': 'Personal',
        'unique': 'Unique'
      }

      const objectiveExclusionObj = {
        'custom': 'Customers'
      }

      const subjectObj = {
        'clients': 'For Clients',
        'client': 'For Clients',
        'customers': 'For Clients',
        'customer': 'For Clients',
        'employees': 'For Employees',
        'employee': 'For Employees',
        'executives': 'For Executives',
        'executive': 'For Executives',
        'discerning': 'For Discerning Companies',
        'companies': 'For Discerning Companies',
        'companie': 'For Discerning Companies',
        'company': 'For Discerning Companies'
      }

      let objective = 'Luxury'
      let subject = ''
      for (const i in objectiveObj) {
        if (urlString.includes(i) && ((objectiveExclusionObj[i] && !urlString.includes(objectiveExclusionObj[i])) || !objectiveExclusionObj[i])) {
          objective = objectiveObj[i]
          break
        }
      }

      for (const i in subjectObj) {
        if (urlString.includes(i)) {
          subject = subjectObj[i]
          break
        }
      }

      return i18n?.t('dynamicLandingTitle', { objective, subject })
    } else {
      return false
    }
  }

  /**
  * Track Event Queue
  * Login in ServiceCodes.vue
  * example: trackEventQueue.value.push({ ecommerce: null })
  * 
  * 1. We create a queue of events
  * 2. When GTM is ready, it starts processing this queue
  * This allows events to be captured even before GTM is initialized
  */
  const trackEventQueue = useState('trackEventQueue', () => [])

  /**
  * Room Id for Websokets
  */
  const useRoomId = () => useState('roomId', () => {
    const storedRoomId = sessionStorage.getItem('roomId')

    if (storedRoomId) {
      return storedRoomId
    }

    const newRoomId = uuidv4()
    sessionStorage.setItem('roomId', newRoomId)
    return newRoomId
  })

  /*
  * Logic of splitting XLSX-files into chunks
  */
  const useXLSXChunks = async (globalOptions = {}) => {
    const XLSX = await import('xlsx')

    const progress = ref({
      totalRecords: 0,
      processedRecords: 0,
      remainingRecords: 0,
      percentage: 0
    })

    const responseData = ref([])

    const errors = ref([])

    /**
    * Convert Array у XLSX-файл.
    */
    const convertToXLSXFile = (chunk, filename) => {
      const workbook = XLSX.utils.book_new()
      const sheet = XLSX.utils.aoa_to_sheet(chunk)
      XLSX.utils.book_append_sheet(workbook, sheet, 'Sheet1')

      const buffer = XLSX.write(workbook, { bookType: 'xlsx', type: 'buffer' })
      return new File([buffer], filename, { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' })
    }

    /**
    * Reads an XLSX file and returns data as an array.
    */
    const parseXLSXFile = async (file) => {
      try {
        const buffer = await file.arrayBuffer()
        const workbook = XLSX.read(buffer, { type: 'buffer', cellDates: true })
        const sheetName = workbook.SheetNames[0]
        const sheet = workbook.Sheets[sheetName]
        return XLSX.utils.sheet_to_json(sheet, { header: 1 })
      } catch (err) {
        return { status: 'error', errors: err }
      }
    }

    /**
    * Downloads the file, breaks it into chunks, and sends it to the backend.
    */
    const uploadFile = async (file, callback, options) => {
      const records = await parseXLSXFile(file)
      let isCriticalError = false

      // Checking the number of technical lines
      let technicalRowsNum = 0
      for (const i in records) {
        const row = records[i]
        if (/^\(.*\)$|^\[.*\]$/.test(row[0]) || /^\(.*\)$|^\[.*\]$/.test(row[1])) {
          technicalRowsNum++
        } else {
          break
        }
      }

      if (records?.status === 'error' || records.length <= technicalRowsNum) {
        errors.value.push(i18n?.t('fileIsCorruptedFormat'))
        isCriticalError = true
      } else {
        // We keep the first two lines (technical)
        const technicalRows = records.slice(0, technicalRowsNum)

        // Removing technical lines from master data calculation
        const recordsWithoutHeader = records.slice(technicalRowsNum)
        const totalRecords = recordsWithoutHeader.length

        if (totalRecords === 0) {
          errors.value.push(i18n?.t('theFileIsEmpty'))
          isCriticalError = true
        } else {
          const chunkSize = globalOptions?.chunkSize ? globalOptions?.chunkSize : 100
          const totalChunks = Math.ceil(totalRecords / chunkSize)
          let processedRecords = 0

          for (let i = 0; i < totalChunks; i++) {
            // Main chunk
            const chunk = recordsWithoutHeader.slice(i * chunkSize, (i + 1) * chunkSize)

            // Add technical lines to each chunk
            const chunkWithHeader = [...technicalRows, ...chunk]

            try {
              const fileChunk = convertToXLSXFile(chunkWithHeader, `chunk-${i + 1}-of-${totalChunks}.xlsx`)
              const formData = new FormData()
              formData.append('file', fileChunk)

              const response = await callback(formData, options)

              if (!response.success) {
                errors.value.push(`${i18n?.t('serverRejectedTheFile')}: ${response.statusText}`)
                isCriticalError = true
              }
              if (response.data?.errors && response.data.errors[0]?.errors[0]?.includes('This is an old file structure.')) {
                processedRecords = 0
                isCriticalError = true
              } else {
                processedRecords += chunk.length
              }

              // Updating progress
              progress.value = {
                totalRecords,
                processedRecords,
                remainingRecords: totalRecords - processedRecords,
                percentage: ((processedRecords / totalRecords) * 100).toFixed(0),
                chunkSize,
                isCriticalError
              }

              responseData.value.push(response.data)

              if (isCriticalError) { 
                break
              }
            } catch (error) {
              console.log('error', error)
              errors.value.push(`${i18n?.t('errorSendingChunk')} ${i + 1}: ${JSON.stringify(error)}`)
            }
          }
        }
      }

      if (errors.value.length) {
        return { status: 'success'}
      } else {
        return { status: 'error', errors: errors.value }
      }
    }

    return { progress, uploadFile, data: responseData, errors }
  }

  /**
  * Return Object
  */
  return {
    useCustomCookie,
    setMetaTags,
    getDynamicTitle,
    trackEventQueue,

    useRoomId,

    useXLSXChunks
  }
}

/*
* EventQueue
* Deferred UI emitter with internal queue.
* Allows calling toast (or similar UI actions) before the actual instance is ready.
* Useful for safely triggering UI feedback from anywhere in the app, even before initialization.
*/
export function useEventQueue() {
  const eventQueue = useState('eventQueue', () => [])

  return {
    add: (type, event) => { 
      eventQueue.value.push({ type, event })
    },
    eventQueue
  }
}
